import posthog from 'posthog-js';
import { acceptedCategory } from 'vanilla-cookieconsent';
import { hasPostHogPermission } from '@/shared/services/analytics/posthogConsent';
import { resolveTrackingDestinations } from '@/shared/services/analytics/trackingConsent';

/**
 * Centrální tracking helper.
 *
 * Posílá event do GTM dataLayer (→ GA4 + Google Ads) a PostHogu.
 * Respektuje consent: dataLayer vyžaduje analytics nebo advertisement consent,
 * PostHog respektuje samostatné pravidlo pro zaměstnance.
 *
 * Hodnoty držet konzistentní napříč voláními (snake_case, max 40 znaků pro GA4).
 */

declare global {
    interface Window {
        dataLayer ?: Record<string, unknown>[];
    }
}

export type CommerceEvent =
    | 'calculator_result_viewed'
    | 'lead_form_submit'
    | 'order_submitted'
    | 'phone_click'
    | 'registration_complete';

export type EngagementEvent =
    | 'login'
    | 'cta_click';

export type EventName = CommerceEvent | EngagementEvent;
export type EventPayload = Record<string, string | number | boolean | null | undefined>;

type ProductEvent =
    | 'form_viewed'
    | 'form_started'
    | 'form_submitted'
    | 'form_abandoned'
    | 'registration_started'
    | 'registration_submit_attempted'
    | 'registration_validation_failed'
    | 'registration_failed'
    | 'calculation_started'
    | 'calculation_succeeded'
    | 'calculation_failed'
    | 'calculation_validation_failed'
    | 'calculation_offer_selected'
    | 'order_submit_attempted'
    | 'order_validation_failed'
    | 'order_failed';

// Diagnostika patří pouze do produktové analytiky, nikoli do reklamních konverzí.
export function trackProductEvent(event : ProductEvent, payload : EventPayload = {}) : boolean {
    try {
        if (typeof window === 'undefined' || !posthog.__loaded || !hasPostHogPermission()) {
            return false;
        }

        return Boolean(event === 'form_abandoned'
            ? posthog.capture(event, payload, { transport: 'sendBeacon', send_instantly: true })
            : posthog.capture(event, payload));
    } catch {
        // Nedostupná analytika nesmí ovlivnit vyplnění ani odeslání formuláře.
        return false;
    }
}

type LeadFormPayload =
    | { lead_type : 'public_calc' }
    | {
        lead_type : 'front_demand';
        inquiry_type : string;
        has_company : boolean;
        has_message : boolean;
    };

type NonConversionEvent = Exclude<EventName, 'lead_form_submit' | 'registration_complete'>;

export function track(event : 'lead_form_submit', payload : LeadFormPayload) : void;
export function track(event : 'registration_complete') : void;
export function track(event : NonConversionEvent, payload ?: EventPayload) : void;

export function track(event : EventName, payload : EventPayload = {}) : void {
    try {
        if (typeof window === 'undefined') {
            return;
        }

        const destinations = resolveTrackingDestinations({
            analytics: acceptedCategory('analytics'),
            advertisement: acceptedCategory('advertisement'),
        });

        if (destinations.dataLayer) {
            window.dataLayer = window.dataLayer || [];
            // Event name is reserved and cannot be overwritten by a payload field.
            window.dataLayer.push({ ...payload, event });
        }

        if (hasPostHogPermission() && posthog.__loaded) {
            posthog.capture(event, payload);
        }
    } catch (err) {
        // Tracking nikdy nesmí rozbít business flow (form submit, redirect).
        console.warn('[track] failed:', event, err);
    }
}
